import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { AlertTriangle, ArrowRight } from "lucide-react";
import { toast } from "sonner";
import api, { errMsg } from "../../lib/api";
import { DashboardShell } from "../../components/DashboardShell";
import { StatusPill } from "../../components/StatusPill";

export default function ClientChart() {
  const { clientId } = useParams();
  const navigate = useNavigate();
  const [chart, setChart] = useState(null);

  useEffect(() => {
    api.get(`/clinician/clients/${clientId}`).then((r) => setChart(r.data)).catch((e) => toast.error(errMsg(e)));
  }, [clientId]);

  if (!chart) return <DashboardShell title="Client chart"><div className="h-8 w-8 rounded-full border-2 border-[#1E3F20] border-t-transparent animate-spin" /></DashboardShell>;

  return (
    <DashboardShell title={chart.client?.name} subtitle={`Client chart · ${chart.client?.email} · client since ${new Date(chart.client?.created_at).toLocaleDateString()}`}>
      <div className="grid grid-cols-1 lg:grid-cols-4 gap-8">
        <aside className="card-warm p-6 h-fit" data-testid="client-chart-info">
          <p className="label-caps text-[#7E9B82] mb-4">Summary</p>
          <div className="space-y-3 text-sm">
            <div><p className="text-xs text-[#5C5C57] uppercase tracking-wide font-bold">Evaluations</p>
              <p className="font-heading text-3xl font-semibold">{chart.cases.length}</p></div>
            <div><p className="text-xs text-[#5C5C57] uppercase tracking-wide font-bold">In-platform interviews</p>
              <p className="font-heading text-3xl font-semibold">{chart.interview_count}</p></div>
          </div>
        </aside>
        <div className="lg:col-span-3 space-y-4" data-testid="client-chart-cases">
          {chart.cases.map((c) => (
            <div key={c.id} className="card-warm p-6 cursor-pointer hover:-translate-y-0.5 transition-transform"
              onClick={() => navigate(`/clinician/cases/${c.id}`)} data-testid={`chart-case-${c.id}`}>
              <div className="flex flex-wrap items-center justify-between gap-3">
                <div>
                  <p className="font-heading text-xl font-semibold tracking-tight">{c.product?.name}</p>
                  <p className="text-xs text-[#5C5C57] mt-1">
                    Opened {new Date(c.created_at).toLocaleDateString()}
                    {c.appointment ? ` · Interview ${new Date(c.appointment.start).toLocaleString()}` : ""}
                  </p>
                </div>
                <div className="flex items-center gap-3">
                  {c.flags?.length > 0 && (
                    <span className="inline-flex items-center gap-1.5 text-[#8C2A2A] text-xs font-semibold">
                      <AlertTriangle className="h-3.5 w-3.5" /> {c.flags.length} flag{c.flags.length > 1 ? "s" : ""}
                    </span>
                  )}
                  <StatusPill status={c.status} />
                  <ArrowRight className="h-4 w-4 text-[#7E9B82]" />
                </div>
              </div>
              {c.responses?.length > 0 && (
                <div className="flex flex-wrap gap-2 mt-4">
                  {c.responses.map((r) => (
                    <span key={r.measure} className="text-[11px] font-bold px-2.5 py-1 rounded-full bg-[#E3DAC9]/50 text-[#5C5C57]"
                      data-testid={`chart-score-${c.id}-${r.measure}`}>
                      {r.measure_name} {r.score}{r.max_score ? `/${r.max_score}` : ""}
                    </span>
                  ))}
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
    </DashboardShell>
  );
}
